<script setup>
  import InstallationOfficeList from "@/components/InstallationOfficeParts/InstallationOfficeList"
  import InstallationOfficeSerchForm from "@/components/InstallationOfficeParts/InstallationOfficeSerchForm"
  import Api from "@/services/api/ApiServiceFabrick"
  import PaginateWithCount from "@/components/PaginateWithCount.vue"
  import AuthorityCacheApi from "@/services/models/AuthorityCacheApi"
  import FileDownloadBtn from "@/components/Files/DownloadButton"
  import FileUploadModal from "@/components/Files/UploadModal"
  import { reactive, ref, onMounted, watch } from 'vue'
  import { useAuth } from "@/services/user/Auth0UserProvider"
  import { useRouter } from 'vue-router'
  import i18n from "@/lang/i18n"
  import { saveDataToSessionStorage, loadDataFromSessionStorage, clearSessionStorage } from "@/services/functions/sessionStorage";
  import { MANAGEMENT_OFFICE_TYPE, SUB_MANAGEMENT_OFFICE_TYPE } from "@/services/consts.ts"

  const props = defineProps({
    showFlash: {
      type: Function
    }
  })

  document.title = i18n.global.t('pages.installationOffices.title.list')
  const changeStatusAuthority = ref(false)
  const createAuthority = ref(false)
  const viewAuthority = ref(false)
  const { loginWithRedirect } = useAuth()
  const router = useRouter()
  const form = reactive({})
  const currentPage = ref(1)
  const totalPage = ref(0)
  const itemsPerPage = ref(50)
  const allElementsCount = ref(0)
  const installationOffices = ref([])
  const listLoading = ref(false)
  const isSearchCompleted = ref(false)
  const searchParams = reactive({
    page: 1,
    withs: 'updatedBy',
    status: 'only-active'
  })
  const updatingStatus = ref(false)
  const response = reactive({errors: {}})
  const modalState = ref(false)
  const csvImportOperationAuthority = ref(false)
  const csvExportOperationAuthority = ref(false)
  const downloadType = "App\\Services\\Csv\\Exports\\InstallationOfficeService"
  const uploadType = "App\\Services\\Csv\\Imports\\InstallationOfficeService"
  const isManagementOffice = ref(false)
  const isSubManagementOffice = ref(false)

  onMounted(() => {
    getAuthorityList()
    listLoading.value = true
    Api().getSelfAccount().then((data) => {
      if (data.login_organization_type == MANAGEMENT_OFFICE_TYPE) {
        isManagementOffice.value = true
      }
      if (data.login_organization_type == SUB_MANAGEMENT_OFFICE_TYPE) {
        isSubManagementOffice.value = true
      }
      const savedSearchParams = loadDataFromSessionStorage('installationOfficeSearchParams')
      if (savedSearchParams) {
        getInstallationOffices(savedSearchParams)
      } else if (!isManagementOffice.value) {
        getInstallationOffices(searchParams)
      } else {
        listLoading.value = false
      }
    }).catch((error) => {
      const res = error.response
      if (res.status == 401) {
        loginWithRedirect({ appState: { targetUrl: location.pathname } })
      }
      listLoading.value = false
    })
  })

  const getAuthorityList = async(isError = false) =>{
    if (isError) {
      await AuthorityCacheApi.reflesh()
    }
    const authorityList = await AuthorityCacheApi.getWithParse()
    changeStatusAuthority.value = authorityList["octlink.installation_office.delete"]
    createAuthority.value = authorityList["octlink.installation_office.create"]
    viewAuthority.value = authorityList["octlink.installation_office.read"]
    csvImportOperationAuthority.value = authorityList["octlink.installation_office.input"];
    csvExportOperationAuthority.value = authorityList["octlink.installation_office.output"];
  }

  function getInstallationOffices (params) {
    listLoading.value = true
    Api().getPaginatedInstallationOffices(params).then((data) => {
      installationOffices.value = data.installationOffices
      currentPage.value = data.current_page
      totalPage.value = data.total_page
      allElementsCount.value = data.total
      itemsPerPage.value = data.per_page
      listLoading.value = false
      isSearchCompleted.value = true
    }).catch((error) => {
      return errorHandle(error.response)
    })
  }

  function searchInstallationOfficesList (form) {
    searchParams.page = 1
    searchParams.management_officeable_id = form?.managementOffice?.id,
    searchParams.management_officeable_type = form?.managementOffice?.type,
    searchParams.company_name = form?.companyName
    searchParams.status = form?.status
    searchParams.sort
    searchParams.sort_type
    saveDataToSessionStorage('installationOfficeSearchParams', searchParams)
    getInstallationOffices(searchParams)
  }

  function handleChangeSearchForm (serchForm) { form.value = serchForm}

  function changeSortOrder (sortItem, sortType) {
    searchParams.sort = sortItem
    searchParams.sort_type = sortType
    if (currentPage.value == 1) {
      getInstallationOffices(searchParams)
    } else {
      currentPage.value = 1;
    }
  }

  function updateStatus (params, key) {
    updatingStatus.value = true
    Api().updateInstallationOfficeStatus(params.id, params).then((_data) => {
      props.showFlash(i18n.global.t('pages.common.messages.changeStatusSuccess'), "success")
      updatingStatus.value = false
      getInstallationOffices(searchParams)
    }).catch((error) => {
      errorHandle(error.response)
      if (error.response.status == 403) {
        getAuthorityList(true)
      }
    })
  }

  function moveToForm () {
    router.push('/installation-offices/form/')
  }

  function inactivateModalState (isReacquireAccount) {
    modalState.value = false
    if (isReacquireAccount) {
      getInstallationOffices(searchParams)
    }
  }

  function activateModalState () {
    modalState.value = true
  }

  function errorHandle (res) {
    switch (res.status) {
      case 401:
        loginWithRedirect({ appState: { targetUrl: location.pathname } })
        listLoading.value = false
        break
      case 403:
        props.showFlash(i18n.global.t('pages.common.messages.forbidden'), "error", false)
        listLoading.value = false
        break
      case 422:
        response.errors = res.data.errors
    }
    return updatingStatus.value = false
  }

  function clearInstallationOfficeSearchParams() {
    clearSessionStorage('installationOfficeSearchParams')
  }

  watch(currentPage, () => {
    searchParams.page = currentPage.value
    getInstallationOffices(searchParams)
  })
</script>

<template>
  <div class="d-flex" style="min-height: 45vw;">
    <div class="content p-4 w-100">
      <div class="d-flex">
        <h4 class="card-title mt-0">
          {{ i18n.global.t('pages.installationOffices.title.list') }}
        </h4>
      </div>
      <div class="d-flex justify-end mb-5">
        <div class="btn-area">
          <v-btn v-if="viewAuthority && createAuthority" class="exec-btn mt-0" @click="moveToForm">
            {{ i18n.global.t('pages.common.button.newCreate') }}
          </v-btn>
          <FileDownloadBtn
            :showable="csvExportOperationAuthority"
            btnClass="exec-btn mt-0 ml-1"
            :btnName="i18n.global.t('pages.common.button.csvExport')"
            :type="downloadType"
            :filterItems="searchParams"
          />
          <v-btn v-show="csvImportOperationAuthority" class="exec-btn mt-0 ml-1" @click="activateModalState">
            {{ i18n.global.t('pages.common.button.csvImport') }}
          </v-btn>
        </div>
      </div>
      <div>
        <InstallationOfficeList
          class="mb-2"
          :installationOffices="installationOffices"
          :listLoading="listLoading"
          :changeStatusAuthority="changeStatusAuthority"
          :updatingStatus="updatingStatus"
          :isManagementOffice="isManagementOffice"
          :isSearchCompleted="isSearchCompleted"
          @changeStatus="updateStatus"
          @changeSortOrder="changeSortOrder"
        />
        <PaginateWithCount
          class="justify-end"
          :totalPage="totalPage"
          v-model="currentPage"
          :itemsPerPage="itemsPerPage"
          :allElementsCount="allElementsCount"
          :showItemCount="true"
        />
        <FileUploadModal
          :modalState="modalState"
          :title="i18n.global.t('pages.installationOffices.csv.import')"
          uploadBtnClass='exec-btn mt-0 ml-1'
          closeBtnClass='mt-0'
          :type="uploadType"
          templateCsvFileType="installation_offices"
          @closeModal="inactivateModalState"
        />
        <hr />
      </div>
    </div>
    <InstallationOfficeSerchForm
      :loading="listLoading"
      :isSubManagementOffice="isSubManagementOffice"
      @formchange="handleChangeSearchForm"
      @search="searchInstallationOfficesList"
      @clear="clearInstallationOfficeSearchParams"
    />
  </div>
</template>
